'use strict';

import React from 'react';
import { Box, Card, Grid, Typography } from '@mui/material';
import { Brand } from '@/lenses/uilenses/theme/colors';

const FALLBACK_GRADIENTS = [
	'linear-gradient(135deg, #284A6F 0%, #855DA3 100%)',
	'linear-gradient(135deg, #855DA3 0%, #FF4F58 100%)',
	'linear-gradient(135deg, #001D38 0%, #284A6F 100%)',
	'linear-gradient(135deg, #FDA851 0%, #FF4F58 100%)',
	'linear-gradient(135deg, #12824a 0%, #284A6F 100%)',
];

/** @typedef {'lg' | 'md' | 'sm'} SizePreset */

/**
 * @param {object} props
 * @param {{ id: string, label: string, description?: string, tileLetter?: string, tileGradient?: string, icon?: import('react').ReactNode }[]} props.options
 * @param {string|null|undefined} props.value
 * @param {(id: string) => void} props.onChange
 * @param {{ xs?: number, sm?: number, md?: number }} [props.columns]
 * @param {'comfortable' | 'compact'} [props.density]
 * @param {SizePreset} [props.sizePreset] — overrides density sizing (lg product, md compact grid, sm goal tiles)
 * @param {string} [props.namePrefix]
 */
export function ChoiceLogoTiles({
	options,
	value,
	onChange,
	columns = { xs: 12, sm: 6, md: 4 },
	density = 'comfortable',
	sizePreset,
	namePrefix = 'option',
}) {
	const preset =
		sizePreset || (density === 'compact' ? 'md' : 'lg');
	const sz =
		preset === 'sm'
			? { letter: 30, pad: 1, spacing: 0.75, stripe: 3, title: 'body2', iconFont: 20, titleWeight: 700 }
			: preset === 'md'
				? { letter: 36, pad: 1.5, spacing: 1, stripe: 3, title: 'caption', iconFont: 22, titleWeight: 800 }
				: { letter: 48, pad: 2, spacing: 1.5, stripe: 4, title: 'subtitle2', iconFont: 28, titleWeight: 800 };

	return (
		<Grid container spacing={sz.spacing}>
			{options.map((o, i) => {
				const selected = value === o.id;
				const letter = o.tileLetter || String(o.label || '').match(/[A-Za-z0-9]/)?.[0] || '?';
				const grad = o.tileGradient || FALLBACK_GRADIENTS[i % FALLBACK_GRADIENTS.length];
				return (
					<Grid item key={o.id} xs={columns.xs ?? 12} sm={columns.sm ?? 6} md={columns.md ?? 4}>
					<Card
						component="button"
						type="button"
						data-testid={`tile-${namePrefix}-${o.id}`}
						onClick={() => onChange(selected ? null : o.id)}
						elevation={0}
						sx={{
							width: '100%',
							height: '100%',
							textAlign: 'left',
							cursor: 'pointer',
							overflow: 'hidden',
							border: '2px solid',
						borderColor: selected ? '#374151' : '#e2e8f0',
						background: selected ? '#374151' : '#fff',
						borderRadius: 3,
						transition: 'all 0.2s ease',
						'&:hover': {
							borderColor: selected ? '#374151' : Brand.molecularBlue,
								boxShadow: selected ? 'none' : '0 4px 16px rgba(0, 29, 56, 0.08)',
								transform: selected ? 'none' : 'translateY(-1px)',
							},
						}}
					>
						<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, p: sz.pad }}>
							<Box
								sx={{
									width: sz.letter,
									height: sz.letter,
									minWidth: sz.letter,
									borderRadius: 2,
									background: selected ? 'rgba(255,255,255,0.2)' : grad,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									color: Brand.pureWhite,
									fontWeight: 800,
									fontSize: preset === 'sm' ? '0.8rem' : '1rem',
									lineHeight: 1,
									'& .MuiSvgIcon-root': { fontSize: sz.iconFont, color: Brand.pureWhite },
								}}
							>
								{o.icon != null ? o.icon : letter}
							</Box>
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<Typography
									variant={sz.title}
									sx={{
										fontWeight: sz.titleWeight,
										color: selected ? Brand.pureWhite : Brand.darkModeBlue,
										lineHeight: 1.3,
										display: 'block',
									}}
								>
									{o.label}
								</Typography>
								{o.description && preset !== 'sm' && (
									<Typography
										variant="caption"
										sx={{
											color: selected ? 'rgba(255,255,255,0.8)' : '#64748b',
											display: 'block',
											mt: 0.35,
											lineHeight: 1.4,
										}}
									>
										{o.description}
									</Typography>
								)}
								{o.description && preset === 'sm' && (
									<Typography
										variant="caption"
										sx={{
											color: selected ? 'rgba(255,255,255,0.75)' : '#64748b',
											display: '-webkit-box',
											WebkitLineClamp: 2,
											WebkitBoxOrient: 'vertical',
											overflow: 'hidden',
											mt: 0.25,
											lineHeight: 1.35,
											fontSize: '0.68rem',
										}}
									>
										{o.description}
									</Typography>
								)}
							</Box>
						</Box>
					</Card>
					</Grid>
				);
			})}
		</Grid>
	);
}
